/**
 * The address of the control a session overlay was opened from, so focus can come back to it.
 *
 * The overlay lives in the URL, so what survives the round trip is a number and an intent — not an
 * element and not a ref. Closing it therefore means finding the opener again in the DOM, and the id
 * ALONE does not identify it: the Przegląd tab has two controls for the same session, the activity
 * row and the `Skomentuj sesję` CTA on the very card the row is about. Restoring by id put focus on
 * whichever came first in the document, which was the CTA whatever the trainer had pressed.
 *
 * The INTENT is what tells them apart, and it is already in the URL as `?comment=`. One function so
 * the format lives in one place: every opener stamps it as a `data-session-opener` attribute and
 * `pages/client-detail`'s screen — which owns the overlay and therefore the restore — looks it up
 * by the same call.
 *
 * ── Why it is in `shared/` ────────────────────────────────────────────────────────────────────
 * It was `pages/client-detail/model/detailTabs` while the detail's own two controls were the only
 * openers. `pages/client-calendar`'s event blocks are the third, and a page may not import another
 * page's model — so the format either moves down a layer or gets copied, and a copied string format
 * is exactly the drift this function exists to prevent.
 */

/**
 * Why a session is being opened, which decides where focus lands once it is.
 *
 * `read` puts focus on the panel's heading — a trainer looking at a session wants to read it.
 * `comment` puts focus in the composer, and comes from exactly one control: the CTA that says
 * `Skomentuj sesję`. Naming the intent rather than passing a boolean is what keeps the call sites
 * readable, since none of them is near the code that acts on it.
 */
export type SessionOpenIntent = 'read' | 'comment';

export function sessionOpenerKey(workoutExecutionId: number, intent: SessionOpenIntent): string {
  return intent === 'comment'
    ? `${String(workoutExecutionId)}:comment`
    : String(workoutExecutionId);
}
